import { useSelector } from 'react-redux';
import { Eye } from 'lucide-react';
import { Card } from '@/components/ui/card';
import PostMeta from '@/components/Post/PostMeta';
import { selectCurrentUser } from '@/redux/auth/auth.slice';
import { selectV2 } from '../v2Upload.slice';
import { pickThumbnailFile } from '../utils/fileHelpers';
import { V2Basics } from '../v2Upload.d';

function PreviewHeader({ multi }: { multi: boolean }) {
  return (
    <div className="flex items-center gap-2 px-3.5 pt-3.5">
      <Eye className="w-4 h-4 text-muted-foreground" />
      <div>
        <div className="font-primary text-sm font-semibold leading-none">
          Live {multi ? 'Group' : 'Project'} Preview
        </div>
        <p className="text-[11px] text-muted-foreground mt-1">
          See how your {multi ? 'group' : 'project'} will appear to others.
        </p>
      </div>
    </div>
  );
}

/** The step that asks for the material. Its field opens on a default, so the
 * preview would otherwise claim an answer the user has not been offered yet. */
const JOB_DETAILS_STEP = 4;

function PreviewMeta({
  basics,
  reachedJobDetails,
}: {
  basics: V2Basics;
  reachedJobDetails: boolean;
}) {
  return (
    <PostMeta
      className="mt-2.5"
      skillLevel={basics.difficulty}
      material={reachedJobDetails ? basics.material : ''}
      category={basics.category}
    />
  );
}

function PreviewBadge({ text }: { text: string }) {
  return (
    <span className="inline-flex items-center text-[10px] font-medium px-2 py-0.5 rounded-full border text-muted-foreground whitespace-nowrap">
      {text}
    </span>
  );
}

/** The listing photo, once one has been chosen. */
function PreviewImage({ src, alt }: { src?: string; alt: string }) {
  if (!src) return null;
  return (
    <div className="px-3.5 pt-3">
      <div className="h-[132px] overflow-hidden rounded-lg bg-muted/50">
        <img src={src} alt={alt} className="h-full w-full object-cover" />
      </div>
    </div>
  );
}

/** Author, and the job type — how the piece is cut is what someone scanning the
 * feed is deciding on. The licence has its own step and its own review cell. */
function PreviewByline({
  username,
  jobType,
}: {
  username: string;
  jobType: string;
}) {
  return (
    <div className="flex items-center gap-2 mt-1">
      <span className="text-xs text-muted-foreground truncate">
        by <span className="font-medium text-foreground">{username}</span>
      </span>
      <span className="grow" />
      {jobType && <PreviewBadge text={jobType} />}
    </div>
  );
}

function PreviewDescription({ text }: { text: string }) {
  if (!text) return null;
  return (
    <p className="mt-2 line-clamp-3 text-[11px] leading-relaxed text-muted-foreground">
      {text}
    </p>
  );
}

// fallow-ignore-next-line complexity
export default function LivePreview() {
  const {
    basics,
    jobDetails,
    files,
    thumbnailFileId,
    maxStep,
    mode,
    groupCover,
    multiStage,
    groupDetails,
  } = useSelector(selectV2);
  const { user } = useSelector(selectCurrentUser);
  // In the group flow `basics` describes the workpiece, not the group.
  const multi = mode === 'multi' && multiStage !== 'workpiece';
  const source = multi ? groupDetails : basics;
  const title = source.title || (multi ? 'Untitled group' : 'Untitled project');

  // A group has no files of its own, so its picture is the cover being chosen.
  // The alt stays the file's name for a post: it describes the image, and the
  // Add-files spec identifies the rail's thumbnail by it.
  const thumb = pickThumbnailFile(files, thumbnailFileId);
  const image = multi ? groupCover?.preview : thumb?.preview;
  const imageAlt = multi ? title : (thumb?.name ?? title);

  return (
    <Card className="overflow-hidden border-border-strong bg-card">
      <PreviewHeader multi={multi} />
      <PreviewImage src={image} alt={imageAlt} />

      <div className="p-3.5">
        <div title={title} className="font-semibold text-base truncate">
          {title}
        </div>

        <PreviewByline
          username={user?.username ?? 'you'}
          jobType={multi ? '' : jobDetails.jobType}
        />
        <PreviewDescription text={source.description} />

        {!multi && (
          <PreviewMeta
            basics={basics}
            reachedJobDetails={maxStep >= JOB_DETAILS_STEP}
          />
        )}
      </div>
    </Card>
  );
}
